import { useState } from 'react';
import { Shield, Wallet, Lock, Zap, Users} from 'lucide-react';

const TokenomicsSection = () => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  
  const distributionData = [
    {
      label: "Liquidity Pool",
      value: 60,
      color: "rgba(74,222,128,0.3)",
      icon: <Lock className="w-5 h-5" />,
      details: {
        description: "Locked liquidity ensures stable trading and price support",
        points: [
          "2-year liquidity lock period",
          "Manual LP additions for sustainable growth"
        ]
      }
    },
    {
      label: "Investors",
      value: 21,
      color: "rgba(74,222,128,0.5)",
      icon: <Users className="w-5 h-5" />,
      details: {
        description: "Early supporters and strategic partners",
        points: [
          "Airdrop post launch"        
        ]
      }
    },
    {
      label: "Team",
      value: 4,
      color: "rgba(74,222,128,0.6)",
      icon: <Shield className="w-5 h-5" />,
      details: {
        description: "Development and management incentives",
        points: [
          "24-month vesting schedule",
          "Performance-based unlocks"
        ]
      }
    },
    {
      label: "Ecosystem",
      value: 10,
      color: "rgba(74,222,128,0.7)",
      icon: <Zap className="w-5 h-5" />,
      details: {
        description: "Platform development and community initiatives",
        points: [
          "Staking reward pool",
          "CEX listing support"
        ]
      }
    },
    {
      label: "Rewards",
      value: 5,
      color: "rgba(74,222,128,0.9)",
      icon: <Wallet className="w-5 h-5" />,
      details: {
        description: "Rewards for predictive intelligence contributions",
        points: [         
          "Performance-based prediction rewards",
          "Signal feedback accuracy bonuses"
        ]
      }
    }
  ];

  const tokenInfo = [
    {
      title: "Token Utility",
      items: [
        "Platform access and tier benefits",
        "Fee reduction mechanism",
        "Staking rewards eligibility",
        "Governance participation rights"
      ]
    },
    {
      title: "Tax Structure",
      items: [
        "5% total transaction tax",
        "2% to marketing",
        "2% to development",
        "1% to buy backs and rewards"
      ]
    },
    {
      title: "Security Measures",
      items: [
        "Ownership retained for updates", 
        "Regular security audits",
        "Time-locked team tokens",
        "Automated security checks"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-green-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Tokenomics</h2>
          <p className="text-green-400/70 max-w-2xl mx-auto">
            Built for sustainable growth with fair distribution and strong utility
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Token Overview Card */}
          <div className="bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-6 h-6" />
              <h3 className="text-xl font-bold">Token Overview</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                <span className="text-green-300/70">Name</span>
                <span className="text-green-400 font-mono">opTrade AI</span>
              </div>
              <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                <span className="text-green-300/70">Symbol</span>
                <span className="text-green-400 font-mono">$OPTR</span>
              </div>
              <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                <span className="text-green-300/70">Total Supply</span>
                <span className="text-green-400 font-mono">1,000,000</span>
              </div>
              <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                <span className="text-green-300/70">Network</span>
                <span className="text-green-400 font-mono">Ethereum</span>
              </div>
            </div>
          </div>

          {/* Distribution Visualization */}
          <div className="lg:col-span-2 bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30 p-6">
            <h3 className="text-xl font-bold mb-6">Token Distribution</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {distributionData.map((item) => (
                <div
                  key={item.label}
                  className={`p-4 rounded-lg border transition-all duration-300 cursor-pointer
                    ${selectedCategory === item.label 
                      ? 'border-green-500 bg-green-500/10' 
                      : 'border-green-500/30 hover:border-green-500/50'}`}
                  onClick={() => setSelectedCategory(selectedCategory === item.label ? null : item.label)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded bg-green-500/10">
                      {item.icon}
                    </div>
                    <div>
                      <div className="font-bold">{item.label}</div>
                      <div className="text-green-400 font-mono">{item.value}%</div>
                    </div>
                  </div>
                  
                  {selectedCategory === item.label && (
                    <div className="mt-4 space-y-2 animate-fadeIn">
                      <p className="text-sm text-green-300/70">{item.details.description}</p>
                      <ul className="space-y-1">
                        {item.details.points.map((point, idx) => (
                          <li key={idx} className="text-sm flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span className="text-green-300/70">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Token Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tokenInfo.map((section) => (
            <div key={section.title} 
                 className="bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30 p-6">
              <h3 className="text-xl font-bold mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="text-green-500/50">⟩</span>
                    <span className="text-green-300/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TokenomicsSection;